import { useState, useRef, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { App } from "antd";
import { CHAT_V2_API } from "./constants";
import { mapChatItem, fetchChatFilters, defaultChatFilterIndex, chatFilterSubConditions, fetchChatFilterSettings } from "./helpers";

const LARGE_LIST_THRESHOLD = 300;

// Reusable fetch/paginate/filter logic for a WATI chat list, scoped to a
// specific channel (or WATI's default channel when channelIds is omitted).
// Used both by WatiChatContext (customer channel, shared across QuerysetV4/
// TaskManagement) and standalone by WhatsappMessages (supplier channel) —
// same logic, deliberately separate state/data per channel, since the two
// channels serve genuinely different volumes and use cases.
//
// `notification` (optional) lets a caller inject an isolated notification
// instance instead of the app-wide shared one — see WatiChatContext's own
// dedicated instance, kept separate from the shared instance other pages
// use with other placements (mixing placements on one shared instance
// corrupts antd's position calculation for some of them).
// `applyChatSettings` (default true) gates whether the admin's Chat
// Settings (allowed filters / default filter / admin exemption) apply to
// this instance at all. Explicit and caller-controlled rather than
// inferred from whether channelIds is present — the supplier channel's own
// channelIds resolves asynchronously (starts undefined on first render,
// same shape as the customer channel omitting it entirely), so presence
// alone can't reliably tell the two apart on that first render. When
// false, this instance skips the wait entirely (see the resolution effect
// below) — no admin restrictions, no delay, always plain "All Chats".
//
// `seed` (optional) lets a caller with applyChatSettings: false show an
// already-loaded background preload instantly instead of waiting on its
// own fetch — see WatiChatContext's supplier-channel preload. Only ever
// consumed when applyChatSettings is false; the customer channel always
// does its own explicit, settings-aware fetch regardless of any seed.
//
// `requireChannelIds` (default false) blocks the resolution effect below
// from running at all until channelIds is a non-empty array. The supplier
// channel's channelIds resolves asynchronously (starts undefined on the
// very first render, before WatiChatContext's fetchChannelAssignments call
// returns) — without this guard, that first render's effect fired a real
// fetch/seed-check with channelIds still undefined, which silently omitted
// the channel filter and pulled back the *other* channel's (customer's)
// chats. That wrong request and the follow-up correct one (once channelIds
// actually resolved) then raced over the network, and whichever landed
// last won — sometimes leaving the wrong channel's chats stuck on screen
// until the component remounted. The customer channel intentionally never
// sets this (it always omits channelIds by design, not "not yet resolved"),
// so it isn't affected.
export function useWatiChatList({ authState, channelIds, notification: injectedNotification, applyChatSettings = true, seed, requireChannelIds = false } = {}) {
  const { notification: sharedNotification } = App.useApp();
  const notification = injectedNotification || sharedNotification;
  const { list: seedList = [], token: seedToken = null, count: seedCount = null } = seed || {};

  const [activeChats, setActiveChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [chatSearch, setChatSearch] = useState("");
  const [chatSearchFilter, setChatSearchFilter] = useState(0);
  const [chatFilters, setChatFilters] = useState([]);
  const [chatFiltersLoading, setChatFiltersLoading] = useState(false);
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const [chatTotalCount, setChatTotalCount] = useState(null);
  // A plain ref, not state — pagination only ever reads this via the ref;
  // routing it through useState+effect previously meant "Load More" clicked
  // right after the initial fetch could still see a stale value.
  const chatContinuationTokenRef = useRef(null);
  const setChatContinuationToken = (token) => { chatContinuationTokenRef.current = token; };

  // Nudge staff toward search/filters instead of endlessly scrolling a huge
  // list — resets whenever a fresh (non-append) fetch starts a new search
  // context. Exempted for the "Unread" filter: someone who's already
  // filtered down to unread chats has done exactly what this nudge would
  // otherwise recommend.
  const largeListWarnedRef = useRef(false);
  const warnIfLargeList = (count) => {
    const filterName = chatFilters[selectedFilterIndex]?.name || "";
    if (/unread/i.test(filterName)) return;
    if (count > LARGE_LIST_THRESHOLD && !largeListWarnedRef.current) {
      largeListWarnedRef.current = true;
      notification.info({
        key: "chat-list-large",
        message: "That's a lot of chats",
        description: `You've loaded over ${LARGE_LIST_THRESHOLD} chats — try searching by name/number or picking a filter instead of scrolling through all of them.`,
        placement: "top",
        duration: 8,
      });
    }
  };

  // filterList (optional) overrides the chatFilters state — needed right
  // after resolving filters for the first time, since setChatFilters(list)
  // doesn't apply synchronously: calling fetchActiveChats in the same tick
  // would otherwise still see the old (empty) chatFilters state and silently
  // fall back to filterType 0/no subConditions, i.e. unfiltered "All Chats",
  // regardless of which index was resolved as the default.
  const currentFilterParams = (filterIdx = selectedFilterIndex, filterList = chatFilters) => {
    const f = filterList[filterIdx];
    return { filterType: f?.filterType ?? 0, subConditions: chatFilterSubConditions(f) };
  };

  const buildChatV2Payload = ({ search, searchOptionType, continuationToken = "", filterIdx, filterList }) => {
    const trimmed = (search || "").trim();
    const { filterType, subConditions } = currentFilterParams(filterIdx, filterList);
    const isMsgSearch = searchOptionType === 1 && trimmed;
    return {
      userId: authState?.id,
      continuationToken,
      // Customer UI omits this entirely (backend's own default applies,
      // matching how it's always worked); supplier UI passes its channel
      // explicitly, resolved from uzd_misc mode 'wati_channel_assignments'.
      ...(channelIds ? { channelIds } : {}),
      filterType,
      subConditions,
      searchString: trimmed || null,
      searchOptionType: trimmed ? searchOptionType : null,
      ...(isMsgSearch ? { from: dayjs().subtract(1, "year").toISOString(), to: dayjs().toISOString() } : {}),
    };
  };

  const fetchActiveChats = async (search = chatSearch, filter = chatSearchFilter, filterIdx = selectedFilterIndex, filterList) => {
    largeListWarnedRef.current = false;
    setChatsLoading(true);
    setHasMoreChats(true);
    try {
      const payload = buildChatV2Payload({ search, searchOptionType: filter, continuationToken: "", filterIdx, filterList });
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const mapped = raw.map(mapChatItem);
        setActiveChats(mapped);
        const token = res.data.data?.continuationToken || null;
        setChatContinuationToken(token);
        setHasMoreChats(!!token);
        setChatTotalCount(res.data.data?.count ?? null);
        warnIfLargeList(mapped.length);
        setChatsLoading(false);
        return mapped;
      }
    } catch {}
    setChatsLoading(false);
    return [];
  };

  const loadMoreChats = async () => {
    if (chatsLoadingMore || !hasMoreChats || chatsLoading) return;
    setChatsLoadingMore(true);
    try {
      const payload = buildChatV2Payload({ search: chatSearch, searchOptionType: chatSearchFilter, continuationToken: chatContinuationTokenRef.current || "" });
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const token = res.data.data?.continuationToken || null;
        setChatContinuationToken(token);
        let nextCount = 0;
        setActiveChats(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const merged = [...prev, ...raw.map(mapChatItem).filter(c => !existingIds.has(c.id))];
          nextCount = merged.length;
          return merged;
        });
        setHasMoreChats(!!token);
        warnIfLargeList(nextCount);
      }
    } catch {}
    setChatsLoadingMore(false);
  };

  // Chat filter dropdown ("All Chats" header) — populate from WATI's saved
  // filter tabs (account-wide, not channel-scoped). When applyChatSettings
  // is true (the customer channel — QuerysetV4/TaskManagement), narrowed
  // down to the admin's "allowed filters" list (Global Settings → Chat
  // Settings) if one is configured, default to the admin's configured
  // default filter, falling back to whichever tab WATI marks isDefault.
  // Admins are exempt from both restrictions unless the admin explicitly
  // opted themselves in via "Include admins" in that same settings screen.
  //
  // The customer channel's very first chat list render always waits for
  // this to resolve, then does its own explicit, settings-aware fetch — no
  // fast-path from a background preload. There used to be one (skip the
  // fetch and reuse an already-loaded "All Chats" preload when that
  // happened to match the resolved default), but it was a repeated source
  // of races where the page briefly or permanently showed unfiltered chats
  // regardless of what was actually configured. One extra request on load
  // is a small price for this being unconditionally correct.
  //
  // The supplier channel (WhatsappMessages, applyChatSettings: false) is a
  // separate audience with none of this: no admin restrictions, and no
  // waiting either — its chat list displays immediately from the seed
  // (an already-running background preload) or, failing that, its own
  // immediate unfiltered fetch, entirely independent of the filter-tabs
  // resolution below (which only populates its dropdown for later manual
  // switching, and never blocks or re-triggers the initial display).
  useEffect(() => {
    if (!authState?.id) return;
    if (requireChannelIds && !(channelIds?.length > 0)) return;
    setChatFiltersLoading(true);

    if (!applyChatSettings) {
      if (seedList.length > 0) {
        setActiveChats(seedList.map(mapChatItem));
        setHasMoreChats(seedList.length >= 100);
        chatContinuationTokenRef.current = seedToken;
        setChatTotalCount(seedCount);
      } else {
        fetchActiveChats(chatSearch, chatSearchFilter, 0, []);
      }
    }

    Promise.all([
      fetchChatFilters(authState.id),
      applyChatSettings ? fetchChatFilterSettings() : Promise.resolve({ allowed_filters: [], default_filter: null, include_admins: false }),
    ]).then(([fullList, settings]) => {
      const isExemptAdmin = applyChatSettings && !!authState?.user?.admin && !settings.include_admins;
      const allowed = isExemptAdmin ? [] : settings.allowed_filters;
      const defaultFilter = isExemptAdmin ? null : settings.default_filter;
      const list = allowed.length > 0 ? fullList.filter(f => allowed.includes(f.name)) : fullList;
      setChatFilters(list);
      let idx = 0;
      if (list.length > 0) {
        const defaultIdx = defaultFilter ? list.findIndex(f => f.name === defaultFilter) : -1;
        idx = defaultIdx >= 0 ? defaultIdx : defaultChatFilterIndex(list);
        setSelectedFilterIndex(idx);
      }
      if (applyChatSettings) {
        // Pass `list` explicitly — setChatFilters(list) above hasn't
        // applied yet (state updates aren't synchronous), so without this
        // override fetchActiveChats would read the stale pre-resolution
        // chatFilters state and silently fetch unfiltered "All Chats"
        // instead.
        fetchActiveChats(chatSearch, chatSearchFilter, idx, list);
      }
    }).finally(() => setChatFiltersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.id, channelIds?.join(",")]);

  const onSelectFilter = (idx) => {
    setSelectedFilterIndex(idx);
    fetchActiveChats(chatSearch, chatSearchFilter, idx);
  };

  // Reset this instance back to WATI's own default filter tab ("All Chats",
  // in practice) — used by callers that want an explicit "back to the
  // default view" action rather than preserving whatever filter is
  // currently selected. See WatiChatContext's global reconnect handling,
  // which resets the supplier channel this way but leaves the customer
  // channel on its current filter.
  const resetToDefaultFilter = () => {
    const idx = chatFilters.length > 0 ? defaultChatFilterIndex(chatFilters) : 0;
    setSelectedFilterIndex(idx);
    fetchActiveChats(chatSearch, chatSearchFilter, idx);
  };

  return {
    activeChats, setActiveChats,
    chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
    chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
    chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
    setChatContinuationToken, chatContinuationTokenRef,
    buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter, resetToDefaultFilter,
  };
}
